import { FC, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import HomeTitle from './components/HomeTitle'
import AdvertCard from './components/AdvertCard'
import BirthdaysCard from './components/BirthdaysCard'
import CarePlansCard from './components/CarePlansCard'
import CarersCard from './components/CarersCard'
import IncidentsCard from './components/IncidentsCard'
import MedicationCard from './components/MedicationCard'
import ResidentsCard from './components/ResidentsCard'
import TodosCard from './components/TodosCard'
import Operations from '../../operations'
import theme from '../../LEGACY/theme'
import { history as router } from '../../store'
import { Row } from 'antd'
import Card from './components/Card'
import Footer from '../../LEGACY/Footer'
import { Carer, Home, Log, ResidentItem, User, UserRole } from '@lmc/ts-common'
import { RouterProps } from '@lmc/common/types'
import { OnboardingCard } from './components/OnboardingCard'
import NoticeBanner from './components/NoticeBanner'
import { useIntroStep } from './hooks'
import { isNoticeboardGranted } from 'components/navigation/utils'
import FamilyCard from './components/FamilyCard'
import useFeatureFlags from '../../hooks/useFeatureFlags'
import { CaseIncidentsCard } from './components/CaseIncidentsCard'
import MonitoringHomeCard from 'screens/Rostering/Monitoring/HomeCard/MonitoringHomeCard'
import GoalsCard from './components/GoalsCard'
import useHomeType from 'hooks/useHomeType'

type HomeExtended = Home & { onTrial?: boolean }

const HomeScreen: FC = () => {
  const flags = useFeatureFlags({
    flagNames: [
      'incident_reports_home_card',
      'outcomes_and_goals_v2',
      'incident_case_action_assignment'
    ]
  })?.data

  const { hasRota, isPro: hasPro } = useHomeType()

  const features = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const incidentReportsHomeCardEnabled =
    flags.incident_reports_home_card.enabled &&
    features?.features?.incidentReporting?.enabled &&
    hasPro

  const outcomesAndGoalsV2Enabled = flags?.outcomes_and_goals_v2?.enabled

  const hasMounted = useRef(false)

  const dispatch = useDispatch()

  const fetchData = () => {
    dispatch(Operations.loadList('member-benefits'))
  }

  const showNoticeboard = isNoticeboardGranted(features)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchHomeData = (homeId: string) => {
    dispatch(Operations.getHomeDetails(homeId))
  }

  const carers = useSelector(
    (state: { data: { carers: Carer[] } }) => state.data.carers
  )

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const categories = useSelector(
    (state: { data: { [`log-categories`]: object } }) =>
      state.data['log-categories']
  )

  const memberBenefits = useSelector(
    (state: { data: { [`member-benefits`]: object } }) =>
      state.data['member-benefits']
  )

  const residents = useSelector(
    (state: { data: { residents: ResidentItem[] } }) => state.data.residents
  )

  const exampleResidentId = useMemo(
    () => residents?.find((resident) => resident.status === 'dummy')?.id,
    [residents]
  )

  const { step, qrCode, url } = useIntroStep(
    user?.id,
    user.role !== UserRole.seniorCarer
  )

  const hasCarePlans = home && home.carePlans

  const hasTrial = home && (home as HomeExtended)?.onTrial

  const hasFamilySetupComplete = home && home?.familySetupComplete

  const hasEmar = home && home?.eMAR

  const hasFamilyModuleEnabled = home && home?.family

  const showFamilyAdvert = hasFamilyModuleEnabled && !hasFamilySetupComplete

  const hasGoals = outcomesAndGoalsV2Enabled && home?.features?.goals?.enabled

  const showAdvertCard =
    !hasPro || !hasEmar || !hasFamilyModuleEnabled || showFamilyAdvert
  const showOnboardingCard = hasPro && Number(step) < 6

  const isLoading = !carers || !categories || !memberBenefits

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      return
    }
    fetchHomeData(home?.id)
  }, [])

  const renderSeniorCarer = () => (
    <div style={styles.container} className="row">
      <div>
        <div className="row">
          <div className="twelve columns">
            <HomeTitle />
          </div>
        </div>
        <div id="intro-js-step-dashboard">
          <Row gutter={18}>
            <Card id="intro-js-step-residents">
              <ResidentsCard residents={residents} />
            </Card>
            {incidentReportsHomeCardEnabled && (
              <Card id="intro-js-step-case-incidents">
                <CaseIncidentsCard />
              </Card>
            )}
            <Card id="intro-js-step-incidents">
              <IncidentsCard hasPro={hasPro} hasTrial={hasTrial} />
            </Card>
            <Card id="intro-js-step-tasks">
              <TodosCard />
            </Card>
            {hasRota && hasPro && (
              <Card>
                <MonitoringHomeCard />
              </Card>
            )}
            {hasCarePlans && (
              <Card>
                <CarePlansCard
                  isLoadingProp={isLoading}
                  router={router as unknown as RouterProps}
                />
              </Card>
            )}
            <Card>
              <BirthdaysCard
                isLoading={isLoading}
                carers={carers}
                residents={residents}
              />
            </Card>
          </Row>
        </div>
      </div>
    </div>
  )

  if (user.role === UserRole.seniorCarer) {
    return <div>{renderSeniorCarer()}</div>
  }

  return (
    <>
      <div style={{ padding: '0 20px' }}></div>
      <div style={styles.container} className="row">
        {showNoticeboard && <NoticeBanner />}
        <div>
          <div className="row">
            <div className="twelve columns">
              <HomeTitle user={user} />
            </div>
          </div>
          <div id="intro-js-step-dashboard">
            <Row gutter={18}>
              <Card id="intro-js-step-residents">
                <ResidentsCard residents={residents} />
              </Card>
              {incidentReportsHomeCardEnabled && (
                <Card id="intro-js-step-residents">
                  <CaseIncidentsCard />
                </Card>
              )}
              <Card id="intro-js-step-setup">
                <IncidentsCard hasPro={hasPro} hasTrial={hasTrial} />
              </Card>
              <Card id="intro-js-step-carers">
                <CarersCard isLoading={isLoading} carers={carers} />
              </Card>
              <Card id="intro-js-step-tasks">
                <TodosCard hasPro={hasPro} />
              </Card>
              {hasRota && hasPro && (
                <Card>
                  <MonitoringHomeCard />
                </Card>
              )}
              {hasCarePlans && (
                <Card>
                  <CarePlansCard
                    isLoadingProp={isLoading}
                    homeID={home?.id}
                    router={router as unknown as RouterProps}
                    hasPro={hasPro}
                  />
                </Card>
              )}
              {showOnboardingCard && (
                <Card>
                  <OnboardingCard
                    carerId={user?.id}
                    exampleResidentId={exampleResidentId}
                    step={step}
                    qrCode={qrCode}
                    url={url}
                  />
                </Card>
              )}
              {home.eMAR && (
                <Card>
                  <MedicationCard />
                </Card>
              )}
              {hasGoals && (
                <Card>
                  <GoalsCard />
                </Card>
              )}
              {home.onboardingCompleted && (
                <Card>
                  <FamilyCard />
                </Card>
              )}
              {showAdvertCard && <AdvertCard />}
              <Card>
                <BirthdaysCard
                  isLoading={isLoading}
                  carers={carers}
                  residents={residents}
                />
              </Card>
            </Row>
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

const styles = {
  container: {
    padding: '20px',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: theme.container.size.large,
    minHeight: 'calc(100vh - 63px)'
  }
}

export default HomeScreen
